//Common
import { BASE_NAME } from "@/common/constantes";

/*
 * Controle da versão publicada do frontend.
 *
 * O sistema carrega o programa uma vez e depois só troca de tela, sem recarregar a página. Por isso quem deixou
 * a aba aberta continua com a versão antiga depois de uma publicação, e a tela que ainda não tinha sido aberta
 * não é mais encontrada no servidor (o nome dos arquivos muda a cada build).
 *
 * A cada build é gerado o arquivo versao.json com a identificação do build (ver vite.config.js).
 * Comparando com a identificação do build que está rodando (__APP_BUILD__) sabe-se se existe versão nova.
 */

//Identificação do build que está rodando no navegador
export const BUILD_ATUAL = __APP_BUILD__;

//Chave da sessão com o momento da última recarga feita por erro ao carregar uma tela
const CHAVE_RECARGA_ERRO_IMPORT = "siig-recarga-erro-import";

//Tempo mínimo entre duas recargas automáticas (evita ficar recarregando sem parar se o erro continuar)
const INTERVALO_MIN_RECARGA_MS = 60 * 1000;

/*
 * Consulta a identificação do build publicado no servidor.
 * Retorna null se não conseguir consultar (sem rede, ambiente de desenvolvimento, arquivo inexistente).
 */
export async function fn_consultarBuildPublicado() {
  try {
    //O parâmetro com o horário e o no-store garantem que a resposta não venha de cache
    const resposta = await fetch(
      `${BASE_NAME || ""}/versao.json?t=${Date.now()}`,
      { cache: "no-store" },
    );

    if (!resposta.ok) return null;

    const dados = await resposta.json();

    return dados?.build || null;
  } catch {
    return null;
  }
}

//Verifica se existe uma versão publicada diferente da que está rodando no navegador
export async function fn_existeNovaVersao() {
  const buildPublicado = await fn_consultarBuildPublicado();

  return buildPublicado != null && buildPublicado != BUILD_ATUAL;
}

//Verifica se a mensagem é de erro ao carregar o arquivo de uma tela (acontece na aba aberta antes de uma publicação)
export function fn_isErroImport(mensagem) {
  const texto = String(mensagem || "");

  return (
    texto.includes("Failed to fetch dynamically imported module") ||
    texto.includes("Importing a module script failed") ||
    texto.includes("error loading dynamically imported module") ||
    texto.includes("Unable to preload CSS")
  );
}

/*
 * Recarrega a página para buscar a versão publicada, depois de um erro ao carregar o arquivo de uma tela.
 * Só recarrega uma vez a cada intervalo: se o erro continuar depois da recarga, não recarrega de novo
 * (a tela de erro com o botão Atualizar é apresentada).
 * Retorna true se recarregou.
 * @recarregar: Função que recarrega a página (parâmetro para o teste automatizado)
 */
export function fn_recarregarPorErroImport(
  recarregar = () => window.location.reload(),
) {
  try {
    const ultimaRecarga = Number(
      sessionStorage.getItem(CHAVE_RECARGA_ERRO_IMPORT) || 0,
    );

    if (Date.now() - ultimaRecarga < INTERVALO_MIN_RECARGA_MS) {
      return false;
    }

    sessionStorage.setItem(CHAVE_RECARGA_ERRO_IMPORT, String(Date.now()));
  } catch {
    //Sem acesso à sessão do navegador não há como controlar a repetição, então não recarrega sozinho
    return false;
  }

  recarregar();

  return true;
}

/*
 * Registra o tratamento do erro ao carregar o arquivo de uma tela (evento do Vite): recarrega a página
 * para buscar a versão publicada. Chamado uma vez na inicialização do sistema (main.jsx).
 */
export function fn_registrarTratamentoErroImport() {
  window.addEventListener("vite:preloadError", (event) => {
    if (fn_recarregarPorErroImport()) {
      //A página vai ser recarregada, então não propaga o erro para a tela
      event.preventDefault();
    }
  });
}

/*
 * Aviso de nova versão no SIIG Administrativo.
 *
 * Mais de uma pessoa entra com o usuário system, então a versão já vista fica guardada no navegador (localStorage)
 * e não no banco: cada pessoa vê o aviso uma vez no seu navegador. Não depende de criar nada a cada lançamento:
 * a versão vem do package.json, gravada no programa a cada build (__APP_VERSION__, ver vite.config.js).
 */

//Versão do sistema que está rodando no navegador (versão do package.json no build)
export const VERSAO_ATUAL = __APP_VERSION__;

//Chave do navegador com a última versão que o usuário system já viu no SIIG Administrativo
const CHAVE_VERSAO_VISTA_ADM = "siig-adm-versao-vista";

/*
 * Formata a versão para apresentar: o sufixo de versão complementar fica junto do número.
 * Ex: "1.0.17-a" -> "1.0.17a"; "1.0.18" -> "1.0.18"
 */
export function fn_formatarVersao(versao) {
  return String(versao ?? "").replace(/-([a-z]+)$/i, "$1");
}

//Retorna a última versão já vista no SIIG Administrativo neste navegador (null se nunca viu ou não conseguir ler)
export function fn_getVersaoVistaAdm() {
  try {
    return localStorage.getItem(CHAVE_VERSAO_VISTA_ADM);
  } catch {
    return null;
  }
}

//Guarda no navegador a versão vista no SIIG Administrativo
export function fn_salvarVersaoVistaAdm(versao) {
  try {
    localStorage.setItem(CHAVE_VERSAO_VISTA_ADM, versao);
  } catch {
    //Navegador sem permissão para guardar (ex: bloqueio de dados do site): o aviso volta a aparecer, sem erro
  }
}

/*
 * Indica se deve mostrar o aviso de nova versão: o aviso está ligado (parâmetro) e
 * a versão que está rodando é diferente da última vista neste navegador
 */
export function fn_mostrarAvisoVersaoAdm(
  avisoLigado,
  versaoAtual,
  versaoVista,
) {
  return avisoLigado === true && !!versaoAtual && versaoAtual != versaoVista;
}
